import React from "react";
import { motion } from "framer-motion";
import {
  Leaf,
  Droplets,
  EnergyIcon,
  Wind,
  Sun,
  Zap,
  Building2,
  CheckCircle,
  TrendingUp,
  Shield,
  Users,
  Trophy,
  Award,
  ArrowRight,
  Target,
  Lightbulb,
  Recycle,
  Gauge,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const GreenBuilding_Certification_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0f4f3e] via-[#1a6d5c] to-[#2a8b7f] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-400 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Leaf size={18} className="text-emerald-300" />
            <span>Sustainability & Environmental Excellence</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            Green Building Certification
          </h1>
          <p className="text-gray-100 text-base md:text-lg max-w-2xl mx-auto">
            Achieve environmental sustainability, energy efficiency, and
            occupant well-being through internationally recognized green
            building standards and eco-friendly construction practices.
          </p>
        </motion.div>
      </section>

      {/* What Is */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              What Is Green Building Certification?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Green building certification is recognition granted to buildings
                that meet established sustainability criteria. It evaluates
                energy efficiency, water conservation, materials sustainability,
                and indoor environmental quality. Certification provides
                businesses with frameworks to build high-performance buildings
                that reduce carbon footprint and operating costs.
              </p>
              <p className="font-semibold text-lg text-teal-700">
                Global Certification Standards:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "LEED (Leadership in Energy and Environmental Design)",
                  "BREEAM (Building Research Establishment Environmental Assessment)",
                  "WELL Building Standard",
                  "Green Globes",
                  "Living Building Challenge",
                ].map((standard, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="text-teal-600 mt-0.5 flex-shrink-0"
                    />
                    <span>{standard}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Key Features of Green Building Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Sun,
                title: "Energy Efficiency",
                desc: "Efficient HVAC systems, solar energy, advanced insulation, and energy-efficient lighting reduce consumption.",
              },
              {
                icon: Droplets,
                title: "Water Conservation",
                desc: "Low-flow fixtures, rainwater harvesting systems, and efficient landscaping reduce water usage.",
              },
              {
                icon: Leaf,
                title: "Sustainable Materials",
                desc: "Eco-friendly and locally sourced materials reduce environmental impact of construction and operations.",
              },
              {
                icon: Wind,
                title: "Indoor Environmental Quality",
                desc: "Improved air quality, lighting, and thermal comfort enhance health and productivity of occupants.",
              },
              {
                icon: Recycle,
                title: "Waste Management",
                desc: "Sustainable practices including recycling, reuse, and waste reduction during construction and operation.",
              },
              {
                icon: TrendingUp,
                title: "Carbon Footprint Reduction",
                desc: "Measures and reduces building carbon footprint aiming for carbon neutrality through renewable energy.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:border-l-4 hover:border-teal-500 transition-all"
                >
                  <Icon size={32} className="text-teal-600 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Why Green Building Certification Matters for Your Business
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "Environmental Impact Reduction",
                  desc: "Reduces carbon footprint, energy consumption, and waste production, contributing to climate change mitigation.",
                },
                {
                  title: "Cost Savings",
                  desc: "Energy efficiency and water conservation reduce operational costs significantly through lower utility bills.",
                },
                {
                  title: "Regulatory Compliance and Incentives",
                  desc: "Ensures compliance with local environmental regulations and provides access to tax credits and green rebates.",
                },
                {
                  title: "Health and Well-Being",
                  desc: "Improves indoor air quality and comfort, leading to better health outcomes and occupant productivity.",
                },
                {
                  title: "Market Competitiveness",
                  desc: "Enhances reputation and attracts eco-conscious tenants, clients, and investors seeking sustainability.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 p-4 bg-teal-50 rounded-lg border-l-4 border-teal-500"
                >
                  <Star
                    size={24}
                    className="text-teal-600 flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-gradient-to-br from-teal-50 to-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Benefits of Green Building Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Leaf,
                title: "Reduced Environmental Impact",
                desc: "Minimizes energy and water consumption, waste generation, and carbon emissions.",
              },
              {
                icon: TrendingUp,
                title: "Operational Cost Savings",
                desc: "Energy-efficient systems and sustainable materials lead to lower utility and maintenance costs.",
              },
              {
                icon: Building2,
                title: "Enhanced Property Value",
                desc: "Green certified buildings have higher market value and increased tenant demand.",
              },
              {
                icon: Shield,
                title: "Health and Productivity",
                desc: "Non-toxic materials and improved air quality enhance occupant health and productivity.",
              },
              {
                icon: Award,
                title: "Market Competitiveness",
                desc: "Attracts eco-conscious tenants, buyers, and investors prioritizing sustainability.",
              },
              {
                icon: Gauge,
                title: "Continuous Performance Monitoring",
                desc: "Ongoing assessment ensures sustained sustainability and operational excellence.",
              },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
                >
                  <Icon size={36} className="text-teal-600 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900">
              How to Achieve Green Building Certification
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Choose a Certification Standard",
                  desc: "Select appropriate green building standard (LEED, BREEAM, WELL, etc.) based on your region, goals, and project scope.",
                },
                {
                  step: "2",
                  title: "Design and Build for Sustainability",
                  desc: "Ensure building design includes sustainable features: energy-efficient systems, sustainable materials, water conservation, and indoor quality enhancements.",
                },
                {
                  step: "3",
                  title: "Complete a Self-Assessment",
                  desc: "Evaluate sustainability performance against chosen standard and ensure all criteria are met.",
                },
                {
                  step: "4",
                  title: "Documentation and Submission",
                  desc: "Submit required documentation including energy/water data, material sourcing, and waste management practices.",
                },
                {
                  step: "5",
                  title: "Certification Audit and Inspection",
                  desc: "Undergo audit/inspection by accredited third-party certification body to verify compliance.",
                },
                {
                  step: "6",
                  title: "Continuous Monitoring and Improvement",
                  desc: "Maintain sustainability performance through ongoing monitoring and regular re-certification audits.",
                },
              ].map((process, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-teal-50 rounded-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{process.step}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-700">{process.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Industries Benefiting from Green Building Certification
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Commercial Office Buildings",
              "Retail and Shopping Centers",
              "Educational Institutions",
              "Healthcare Facilities",
              "Hospitality and Hotels",
              "Industrial and Manufacturing Facilities",
            ].map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-teal-500 text-center"
              >
                <Building2 size={32} className="text-teal-600 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#0f4f3e] via-[#1a6d5c] to-[#2a8b7f] rounded-2xl p-10 text-white text-center shadow-lg"
          >
            <Leaf size={40} className="mx-auto mb-4 text-emerald-300" />
            <h3 className="text-2xl font-bold mb-3">
              Why Choose MBA Solutions for Green Building Certification?
            </h3>
            <p className="text-teal-100 mb-6 text-sm leading-relaxed">
              MBA Solutions provides expert green building certification
              consultancy, guiding your organization through compliance and
              sustainability excellence.
            </p>
            <div className="bg-white/10 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-emerald-300 flex-shrink-0"
                />
                Green Building Certification Strategy Development
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-emerald-300 flex-shrink-0"
                />
                Sustainability Design and Compliance Support
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-emerald-300 flex-shrink-0"
                />
                Building Performance Assessment
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-emerald-300 flex-shrink-0"
                />
                Energy and Water Efficiency Audits
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-emerald-300 flex-shrink-0"
                />
                Ongoing Sustainability Monitoring and Reporting
              </p>
            </div>
            <div className="space-y-2 text-sm mb-6">
              <p>
                📧 Email:{" "}
                <a
                  href="mailto:atif@mbasolutions.co"
                  className="underline hover:text-emerald-200"
                >
                  atif@mbasolutions.co
                </a>
              </p>
              <p>
                🌐 Website:{" "}
                <a
                  href="https://www.mbasolutions.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-emerald-200"
                >
                  www.mbasolutions.co
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white text-teal-700 font-bold hover:bg-emerald-50 transition-all"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

// Helper component for star icon
const Star = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <polygon points="12 2 15.09 9.26 23 9.26 17.19 14.31 19.29 21.74 12 16.69 4.71 21.74 6.81 14.31 1 9.27 8.91 9.26 12 2" />
  </svg>
);

export default GreenBuilding_Certification_Details;
